import { createRouter, createWebHistory } from "vue-router";
import { useAccountStore } from "@/stores/account";
import { useApiStore } from "@/stores/api";
import { useSrfStore } from "@/stores/srf";
import { pinia } from "@/stores";
import { isEntraAuthActive } from "@/auth/entraAuthService";
import { agenticPddUrl } from "@/utils/agenticPdd";
import Login from "../views/Login.vue";
import LoginCallback from "../views/LoginCallback.vue";
import Logout from "../views/Logout.vue";
import AccessDenied from "../views/fallback-views/AccessDenied.vue";
import * as Sentry from "@sentry/vue";
import errorHandling from "@/mixins/errorHandling";
import Showcase from "@/views/Showcase.vue";
import { routeHistory } from "@/utils/routeHistory";

// route level code-splitting
// this generates a separate chunk (Products.[hash].js) for this route
// which is lazy-loaded when the route is visited.
const routes = [
  // Default page
  { path: "/", redirect: { name: "Dashboard" } },
  {
    path: "/login",
    name: "Login",
    component: Login,
  },
  {
    path: "/login-callback",
    name: "LoginCallback",
    component: LoginCallback,
  },
  {
    path: "/logout",
    name: "Logout",
    component: Logout,
  },
  {
    path: "/access-denied",
    name: "AccessDenied",
    component: AccessDenied,
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("../views/ForgotPassword.vue"),
  },
  {
    path: "/native-sign-in",
    name: "NativeSignIn",
    component: () => import("../views/NativeSignIn.vue"),
  },
  {
    path: "/user-profile",
    name: "UserProfile",
    component: () => import("../views/UserProfile.vue"),
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () => import("../views/Dashboard.vue"),
    meta: { navKey: "dashboard" },
  },
  { path: "/product-hub", redirect: { name: "Products" } },
  {
    path: "/products",
    name: "Products",
    component: () => import("../views/Products.vue"),
    meta: { navKey: "product-issuance" },
  },
  { path: "/product-hub/:isin", redirect: { name: "Product" } },
  {
    path: "/products/:isin",
    name: "Product",
    component: () => import("../views/Product.vue"),
    props: true,
    meta: { navKey: "product-issuance" },
  },
  {
    path: "/dashboard/products/subscriptions/:isin",
    name: "DashboardSubscriptionsProducts",
    component: () => import("../views/Product.vue"),
    meta: { navKey: "dashboard" },
    props: route => ({
      isin: route.params.isin,
      showGraph: false,
      showEvents: false,
      showAccounts: false,
      showRelatedWorkflows: false,
      showTerms: false,
      showInvoices: false,
      showProductTypeDescription: true,
      showSrfPdd: false,
      showFees: false,
      dashboardProduct: true,
    }),
  },
  {
    path: "/dashboard/products/issued/:isin",
    name: "DashboardIssuedProducts",
    component: () => import("../views/Product.vue"),
    meta: { navKey: "dashboard" },
    props: route => ({
      isin: route.params.isin,
      showEvents: false,
      showAccounts: false,
      showRelatedWorkflows: false,
      showTerms: false,
      showInvoices: false,
      showSrfPdd: false,
      showFees: false,
      dashboardProduct: true,
    }),
  },
  {
    path: "/workflows/new",
    name: "NewWorkflow",
    component: () => import("../views/NewWorkflow.vue"),
    meta: { navKey: "create-workflow" },
  },
  {
    path: "/workflows/:type?/:id([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})",
    name: "Workflow",
    component: () => import("../views/Workflow.vue"),
    props: true,
    meta: { navKey: "workflows" },
  },
  {
    path: "/workflows/:type?/:status(pending|active|complete|terminated|all)?",
    name: "Workflows",
    component: () => import("../views/Workflows.vue"),
    props: true,
    meta: { navKey: "workflows" },
  },
  {
    path: "/workflows/:type/batch-process/:status(pending|active|complete|terminated|all|batches)?",
    name: "BatchProcessWorkflows",
    component: () => import("../views/Workflows.vue"),
    props: true,
    meta: { navKey: "workflows" },
  },
  {
    path: "/workflows/:type/bulk-process/:status(pending|active|complete|terminated|all)?",
    name: "QuarterlyFeesBulkProcess",
    component: () => import("../views/Workflows.vue"),
    props: true,
    meta: { navKey: "workflows" },
  },
  {
    path: "/workflows/:type/batch-process/:status(batches)/:id/:step(list)?",
    name: "BatchProcess",
    component: () => import("../views/Workflows.vue"),
    props: true,
    meta: { navKey: "workflows" },
  },
  {
    path: "/workflows/:type/:action(create|clone)/:id?",
    name: "CreateWorkflow",
    component: () => import("../views/Workflow.vue"),
    props: true,
    meta: { navKey: "create-workflow" },
  },
  // the /workflow/display/:id path is used by the G2 Portal
  {
    path: "/workflow/display/:id",
    name: "WorkflowDisplay",
    component: () => import("../views/Workflow.vue"),
    props: true,
    meta: { navKey: "workflows" },
  },
  {
    path: "/order-acceptance-templates",
    name: "OrderAcceptanceTemplates",
    component: () => import("../views/OrderAcceptanceTemplates.vue"),
    meta: { navKey: "manage-order-acceptance" },
  },
  {
    path: "/workflows/order-acceptance-template/:action([a-z]*)/:id([0-9]+)?",
    redirect: { name: "OrderAcceptanceTemplate" },
  },
  {
    path: "/order-acceptance-templates/:action([a-z]*)/:id([0-9]+)?",
    name: "OrderAcceptanceTemplate",
    component: () => import("../views/OrderAcceptanceTemplate.vue"),
    props: true,
    meta: { navKey: "order-acceptance-templates" },
  },
  {
    path: "/workflows/order-acceptance-template/:type?/:action([a-z]*)/:id([0-9]+)?",
    redirect: { name: "OrderAcceptanceTemplate" },
  },
  {
    path: "/order-acceptance-templates/:type?/:action([a-z]*)/:id([0-9]+)?",
    name: "OrderAcceptanceTemplate",
    component: () => import("../views/OrderAcceptanceTemplate.vue"),
    props: true,
    meta: { navKey: "order-acceptance-templates" },
  },
  {
    path: "/planned-coupons",
    name: "PlannedCoupons",
    component: () => import("../views/PlannedCoupons.vue"),
    meta: { navKey: "planned-coupons" },
  },
  {
    path: "/srf",
    name: "SRFRequests",
    component: () => import("../views/SRFRequests.vue"),
    meta: { navKey: "manage-products" },
  },
  {
    path: "/srf/:id",
    name: "SRF",
    component: () => import("../views/SRF.vue"),
    props: true,
    meta: { navKey: "new-srf" },
  },
  {
    path: "/srfv2/:id",
    redirect: { name: "SRF" },
  },
  {
    path: "/srf/legacy/:id",
    name: "SRFLegacy",
    component: () => import("../views/SRFLegacy.vue"),
    props: true,
    meta: { navKey: "manage-products" },
  },
  {
    path: "/srf-lite/:id",
    name: "SRFLite",
    component: () => import("../views/SRFLite.vue"),
    props: true,
    meta: { navKey: "new-srf-lite" },
  },
  {
    path: "/srf-lite-pm/:id",
    name: "BankableAmc",
    component: () => import("../views/SRFLite.vue"),
    props: route => ({ id: route.params.id, variant: "pm" }),
    meta: { navKey: "new-bankable-amc" },
  },
  {
    path: "/srf/raw/:srf_id",
    name: "SRFRaw",
    component: () => import("../views/SRFRaw.vue"),
    props: true,
    meta: { navKey: "manage-products" },
  },
  {
    path: "/srf/pdd/:id/:entityType?/:entityName?",
    name: "PDD",
    component: () => import("../views/PDD.vue"),
    props: true,
    meta: { navKey: "manage-products" },
  },
  {
    path: "/products/:id/viewtermsheet",
    name: "View Termsheet",
    component: () => import("../views/ViewTermsheet.vue"),
    props: true,
    meta: { navKey: "product-issuance" },
  },
  {
    path: "/impersonate",
    name: "Impersonate",
    component: () => import("../views/Impersonate.vue"),
  },
  {
    path: "/choose-function",
    name: "ChooseFunction",
    component: () => import("../views/ChooseFunction.vue"),
  },
  {
    path: "/platform-user-terms",
    name: "PlatformUserTerms",
    component: () => import("../views/PlatformUserTerms.vue"),
  },
  {
    path: "/document-hub",
    name: "DocumentHub",
    component: () => import("../views/DocumentHub.vue"),
    meta: { navKey: "document-hub" },
    children: [
      {
        path: "gentwo-documents",
        name: "NonDigitalDocuments",
        title: "Gentwo Documents",
        showDocumentTypeName: true,
        component: () => import("../views/DocumentHub.vue"),
      },
      {
        path: "issuer-kyc-documents",
        name: "IssuerKYCDocuments",
        title: "Issuer KYC Documents",
        showDocumentTypeName: false,
        component: () => import("../views/DocumentHub.vue"),
      },
    ],
  },
  {
    path: "/help-center",
    name: "Support",
    component: () => import("../views/Support.vue"),
    meta: { navKey: "help-center" },
    children: [
      {
        path: "faq",
        name: "FAQ",
        title: "FAQs",
        component: () => import("../components/support/FAQ.vue"),
      },
      {
        path: "user-guide",
        name: "UserGuide",
        title: "User Guides",
        component: () => import("../components/support/UserGuide.vue"),
      },
      {
        path: "contacts",
        name: "Contacts",
        title: "Contact us",
        component: () => import("../components/support/Contacts.vue"),
      },
    ],
  },
  {
    // Self-registration was removed (GEN-8203); keep old external links landing somewhere useful.
    path: "/onboarding",
    redirect: "/login",
  },
  {
    path: "/onboarding-verification",
    name: "OnboardingSetPassword",
    component: () => import("../views/OnboardingSetPassword.vue"),
    beforeEnter: to => {
      if (
        to.query.emailconfirmed ||
        to.query.passwordconfirmed ||
        (to.query.localUserId && to.query.remoteUserId && to.query.code)
      ) {
        return true;
      } else {
        return "/login";
      }
    },
  },
  {
    path: "/styleguide",
    name: "Showcase",
    component: Showcase,
  },
  {
    path: "/:catchAll(.*)",
    name: "NotFound",
    component: () => import("../views/fallback-views/NotFound.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to) {
    // to handle pages where there's hash navigation
    if (to.hash) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve({
            el: to.hash,
            //top: savedPosition,
            //top: -50,
            behavior: "smooth",
          });
        }, 250);
      });
    } else {
      return {
        top: 0,
        //behavior: "smooth"
      };
    }
  },
});

router.beforeEach((to, from) => {
  const publicPages = [
    "/login",
    "/login-callback",
    "/logout",
    "/forgot-password",
    "/native-sign-in",
    "/access-denied",
    "/onboarding-verification",
  ];
  const documentHubAllowedPages = [
    "/user-profile",
    "/document-hub",
    "/document-hub/gentwo-documents",
    "/document-hub/gentwo-digital-documents",
  ];
  const authRequired = !publicPages.includes(to.path);
  const accountStore = useAccountStore(pinia);
  const loggedIn = accountStore.userLogged;
  // A multi-function user must pick a function before entering the app (no active function chosen yet).
  // Under the Entra bearer flow the switch mechanism (498/OIDC re-challenge) has no equivalent yet
  // (deferred follow-up), so we don't force the chooser: the user proceeds as their primary identity.
  const needsFunctionChoice =
    loggedIn &&
    !isEntraAuthActive() &&
    (accountStore.user?.availableFunctions?.length ?? 0) > 1 &&
    !accountStore.user?.activeFunctionEmail;
  const hasGenTwoProAccess = accountStore.user?.hasGenTwoProAccess;
  const hasSrfAccess =
    accountStore.user?.srfAccess &&
    !accountStore.user?.isUserPayingAgent &&
    !accountStore.user?.isUserIssuer;
  const hasSrfLiteAccess =
    accountStore.user?.isUserGentwo ||
    (accountStore.user?.srfLiteLevelAccess && accountStore.user?.srfLiteLevelAccess !== "None");
  const hasAmcForPmsAccess =
    accountStore.user?.isUserGentwo ||
    (accountStore.user?.amcForPmsLevelAccess &&
      accountStore.user?.amcForPmsLevelAccess !== "None");
  const isIsp = accountStore.user?.isISP;
  const hasDashboardAccess = accountStore.user?.isUserGentwo || accountStore.user?.isClient;
  const hasDocumentHubAccess = accountStore.user?.hasDocumentHubAccess;
  // The platform T&Cs must be accepted before anything else in the app (GEN-7032).
  // LS: only users who can actually enter something are gated — a locked-out user must not record a
  // binding acceptance for a platform they are then denied entry to; they fall through to /access-denied.
  const needsTermsAcceptance =
    loggedIn &&
    accountStore.user?.platformUserTermsAcceptanceRequired &&
    (hasGenTwoProAccess || hasDocumentHubAccess);
  const hasBatchPaymentsAccess = accountStore.user?.canSeeBatchPayments;
  const hasOrderAcceptanceAccess =
    hasGenTwoProAccess &&
    (!accountStore.user?.isUserIssuer || accountStore.user?.isUserGentwo) &&
    accountStore.user?.isOrderAcceptancePayingAgent;

  // trying to access a restricted page + not logged in
  // redirect to login page
  const shouldRedirectToRoot =
    (to.path.startsWith("/onboarding-verification") && loggedIn) ||
    (to.path.startsWith("/forgot-password") && loggedIn) ||
    (to.path.startsWith("/native-sign-in") && loggedIn) ||
    // The lite flows are gated by their own access levels, not the full-eSRF SrfAccessClaim
    (to.path.startsWith("/srf-lite-pm") && !hasAmcForPmsAccess) ||
    (to.path.startsWith("/srf-lite") &&
      !to.path.startsWith("/srf-lite-pm") &&
      !hasSrfLiteAccess) ||
    (to.path.startsWith("/srf") &&
      !to.path.startsWith("/srf-lite") &&
      !hasSrfAccess &&
      !(to.path.startsWith("/srf/pdd") && (isIsp || hasSrfAccess))) ||
    (to.path.startsWith("/order-acceptance-templates/new") && !hasOrderAcceptanceAccess);

  // If we navigate to a new page, assume we don't care about backend 400 errors from some previous page.
  // Runs on every navigation attempt, so it stays before the guard's redirect/allow decision below.
  const apiStore = useApiStore();
  apiStore.clearBackendErrors();
  // Known only once a list or the SRF was loaded; otherwise the PDD page redirects after its first load.
  const pddUrl =
    to.name === "PDD" &&
    agenticPddUrl(apiStore.options, to.params.id, useSrfStore(pinia).isAgenticPddBySrfId[to.params.id]);

  if (authRequired && !loggedIn) {
    return `/login?return_url=${to.path}`;
  } else if (needsTermsAcceptance && authRequired && to.path !== "/platform-user-terms") {
    // authRequired keeps the public pages reachable — above all /logout, so a user who declines the terms
    // can still sign out instead of being trapped here.
    return `/platform-user-terms?return_url=${encodeURIComponent(to.fullPath)}`;
  } else if (to.path === "/platform-user-terms") {
    // Nothing to accept — don't let the interstitial be reached directly.
    return needsTermsAcceptance ? true : "/";
  } else if (needsFunctionChoice && authRequired && to.path !== "/choose-function") {
    // LS: same public-page exemption as the terms branch — above all /logout must stay reachable from
    // every interstitial, or a user who refuses to proceed is trapped in the app.
    return "/choose-function";
  } else if (to.path === "/choose-function") {
    return true;
  } else if (authRequired && !hasGenTwoProAccess && !hasDocumentHubAccess) {
    return "/access-denied";
  } else if (pddUrl) {
    // Before the SRF access check: agentic-pdd asks phoenix itself whether the user may see the SRF.
    window.location.assign(pddUrl);
    return false;
  } else if (shouldRedirectToRoot) {
    return "/";
  } else if (
    !documentHubAllowedPages.includes(to.path) &&
    authRequired &&
    !hasGenTwoProAccess &&
    hasDocumentHubAccess
  ) {
    return "/document-hub";
  } else if (
    (!hasDashboardAccess && to.path === "/dashboard") ||
    (!hasBatchPaymentsAccess && to.path.includes("batch-process"))
  ) {
    return "/workflows";
  } else {
    router.pending = to;
    routeHistory.push(from);
    return true;
  }
});

router.onError(error => {
  Sentry.addBreadcrumb({
    category: "debugging",
    message: `In router.onError. Router pending fullpath is ${router.pending?.fullPath}. Error name is ${error.name}`,
    level: "info",
  });

  if (
    router.pending?.fullPath &&
    (errorHandling.methods.isChunkLoadError(error) ||
      errorHandling.methods.isPreloadCSSError(error))
  ) {
    // try to reload the desired view
    window.location.assign(router.pending.fullPath);
  } else {
    // else, just reload
    window.location.reload();
  }
});

export default router;
